// This hook now uses FavoritesContext to ensure state is shared across all components
import { useFavorites as useContextFavorites } from '@/contexts/FavoritesContext';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Product } from '@/types';

export const useFavorites = () => {
  const { favorites: favoritesContext, isFavorite, addToFavorites, removeFromFavorites } = useContextFavorites();
  const { user } = useAuth();

  // Convert FavoriteItem[] to string[] for backward compatibility
  const favorites = favoritesContext.map(item => item.productId);

  // Toggle favorite - maintains the same API as before
  const toggleFavorite = async (productId: string) => {
    if (!user) return;

    const isFav = isFavorite(productId);

    if (isFav) {
      await removeFromFavorites(productId);
    } else {
      // Fetch minimal product data to add to favorites
      try {
        const { data: product, error } = await supabase
          .from('products')
          .select('*')
          .eq('id', productId)
          .single();

        if (error) throw error;
        if (!product) return;

        // Convert to Product type
        const productData: Product = {
          id: product.id,
          name: product.name,
          description: product.description || '',
          image: product.image,
          price: product.price || 0,
          pointsPrice: product.points_price,
          brandId: product.brand_id || '',
          categoryId: product.category_id || '',
          subcategoryId: product.subcategory_id,
          stock: product.stock || 0,
          additionalImages: product.additional_images
        };

        await addToFavorites({
          productId,
          product: productData,
          dateAdded: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error fetching product for favorites:', error);
      }
    }
  };

  return { favorites, toggleFavorite, isFavorite };
};
